/**
 * Local safety net for unsaved editor content.
 *
 * The editor saves to the server on a debounce. Between a keystroke and that
 * request landing there is a window where a refresh, a crashed tab or a dropped
 * connection loses the work, and the previous code swallowed save failures
 * silently — so the author had no way of knowing.
 *
 * Every change is therefore mirrored into localStorage synchronously, which
 * survives a reload and a browser restart. The copy is deleted as soon as the
 * server confirms the same content, so a draft existing at all is meaningful:
 * it means the server does not have this text.
 */

const PREFIX = "numa_draft:";
const INDEX = "numa_draft_index";
const MAX_AGE_DAYS = 14;
/** localStorage is small; a single draft above this is not worth keeping. */
const MAX_DRAFT_BYTES = 2_000_000;

const key = (thesisId, chapterId) => `${PREFIX}${thesisId}:${chapterId}`;

function readIndex() {
  try {
    return JSON.parse(localStorage.getItem(INDEX) || "[]");
  } catch {
    return [];
  }
}

function writeIndex(entries) {
  try {
    localStorage.setItem(INDEX, JSON.stringify(entries.slice(-80)));
  } catch {
    /* quota or private mode: the index is a convenience, not the data */
  }
}

/** Store the current content. Returns false if it could not be stored. */
export function saveDraft(thesisId, chapterId, html, chapterTitle = "") {
  if (!thesisId || !chapterId) return false;
  const payload = {
    thesisId,
    chapterId,
    chapterTitle,
    html,
    savedAt: new Date().toISOString(),
  };
  const serialised = JSON.stringify(payload);
  if (serialised.length > MAX_DRAFT_BYTES) return false;

  try {
    localStorage.setItem(key(thesisId, chapterId), serialised);
    const index = readIndex().filter((k) => k !== key(thesisId, chapterId));
    index.push(key(thesisId, chapterId));
    writeIndex(index);
    return true;
  } catch {
    // Quota exceeded: drop the oldest drafts and try once more, so a long
    // session cannot permanently disable the safety net.
    try {
      pruneDrafts(true);
      localStorage.setItem(key(thesisId, chapterId), serialised);
      return true;
    } catch {
      return false;
    }
  }
}

export function readDraft(thesisId, chapterId) {
  if (!thesisId || !chapterId) return null;
  try {
    const raw = localStorage.getItem(key(thesisId, chapterId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearDraft(thesisId, chapterId) {
  if (!thesisId || !chapterId) return;
  try {
    localStorage.removeItem(key(thesisId, chapterId));
    writeIndex(readIndex().filter((k) => k !== key(thesisId, chapterId)));
  } catch {
    /* ignore */
  }
}

/** Drop drafts older than the retention window (or the oldest half, if forced). */
export function pruneDrafts(aggressive = false) {
  const index = readIndex();
  const cutoff = Date.now() - MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
  const survivors = [];

  index.forEach((storageKey, position) => {
    let entry = null;
    try {
      entry = JSON.parse(localStorage.getItem(storageKey) || "null");
    } catch {
      entry = null;
    }
    const expired = !entry || new Date(entry.savedAt).getTime() < cutoff;
    const sacrificed = aggressive && position < index.length / 2;
    if (expired || sacrificed) {
      try {
        localStorage.removeItem(storageKey);
      } catch {
        /* ignore */
      }
    } else {
      survivors.push(storageKey);
    }
  });

  writeIndex(survivors);
}

/**
 * Normalised comparison.
 *
 * contenteditable rewrites whitespace and attribute order on every render, so a
 * byte comparison would offer to recover a draft identical to what is on screen.
 */
export function sameContent(a, b) {
  const normalise = (html) =>
    (html || "")
      .replace(/<br\s*\/?>/gi, "<br>")
      .replace(/&nbsp;/g, " ")
      .replace(/\s+/g, " ")
      .replace(/>\s+</g, "><")
      .trim();
  return normalise(a) === normalise(b);
}

/** "2 minuti fa" — drafts are only ever recent, so the coarse form is enough. */
export function describeAge(isoDate) {
  const seconds = Math.max(0, (Date.now() - new Date(isoDate).getTime()) / 1000);
  if (seconds < 60) return "meno di un minuto fa";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minut${minutes === 1 ? "o" : "i"} fa`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} or${hours === 1 ? "a" : "e"} fa`;
  const days = Math.round(hours / 24);
  return `${days} giorn${days === 1 ? "o" : "i"} fa`;
}
